import { supabase } from '../lib/supabase';

export const documentExpirationService = {
  // ============================================================================
  // EXPIRING DOCUMENTS RETRIEVAL
  // ============================================================================

  async getExpiringDocuments(filters = {}) {
    try {
      let query = supabase
        ?.from('employee_documents')
        ?.select(`
          *,
          employees (
            employee_number,
            first_name,
            last_name,
            department,
            position,
            email
          )
        `)
        ?.not('expiry_date', 'is', null)
        ?.in('document_type', ['work_permit', 'visa', 'driver_license', 'passport'])
        ?.gte('days_until_expiry', 0)
        ?.order('days_until_expiry', { ascending: true });

      // Filter by expiration status
      if (filters?.expirationStatus) {
        query = query?.eq('expiration_status', filters?.expirationStatus);
      }

      // Filter by document type
      if (filters?.documentType) {
        query = query?.eq('document_type', filters?.documentType);
      }

      // Filter by department
      if (filters?.department) {
        query = query?.eq('employees.department', filters?.department);
      }

      // Filter by days until expiry range
      if (filters?.maxDaysUntilExpiry) {
        query = query?.lte('days_until_expiry', filters?.maxDaysUntilExpiry);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        employeeId: row?.employee_id,
        employeeNumber: row?.employees?.employee_number,
        employeeName: `${row?.employees?.first_name} ${row?.employees?.last_name}`,
        department: row?.employees?.department,
        position: row?.employees?.position,
        email: row?.employees?.email,
        documentType: row?.document_type,
        documentName: row?.document_name,
        documentCategory: row?.document_category,
        expiryDate: row?.expiry_date,
        daysUntilExpiry: row?.days_until_expiry,
        expirationStatus: row?.expiration_status,
        renewalReminderSent: row?.renewal_reminder_sent,
        renewalReminderSentAt: row?.renewal_reminder_sent_at,
        storagePath: row?.storage_path,
        uploadedAt: row?.uploaded_at
      })) || [];
    } catch (error) {
      console.error('Error getting expiring documents:', error);
      throw error;
    }
  },

  async getExpiredDocuments() {
    try {
      const { data, error } = await supabase
        ?.from('employee_documents')
        ?.select(`
          *,
          employees (
            employee_number,
            first_name,
            last_name,
            department,
            position,
            email
          )
        `)
        ?.not('expiry_date', 'is', null)
        ?.in('document_type', ['work_permit', 'visa', 'driver_license', 'passport'])
        ?.eq('expiration_status', 'expired')
        ?.order('expiry_date', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        employeeId: row?.employee_id,
        employeeNumber: row?.employees?.employee_number,
        employeeName: `${row?.employees?.first_name} ${row?.employees?.last_name}`,
        department: row?.employees?.department,
        position: row?.employees?.position,
        email: row?.employees?.email,
        documentType: row?.document_type,
        documentName: row?.document_name,
        expiryDate: row?.expiry_date,
        daysUntilExpiry: row?.days_until_expiry,
        expirationStatus: row?.expiration_status
      })) || [];
    } catch (error) {
      console.error('Error getting expired documents:', error);
      throw error;
    }
  },

  // ============================================================================
  // STATISTICS AND ANALYTICS
  // ============================================================================

  async getExpirationStatistics() {
    try {
      const { data, error } = await supabase
        ?.from('employee_documents')
        ?.select('expiration_status, document_type')
        ?.not('expiry_date', 'is', null)
        ?.in('document_type', ['work_permit', 'visa', 'driver_license', 'passport']);

      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        expired: data?.filter(d => d?.expiration_status === 'expired')?.length || 0,
        critical: data?.filter(d => d?.expiration_status === 'critical')?.length || 0,
        warning: data?.filter(d => d?.expiration_status === 'warning')?.length || 0,
        upcoming: data?.filter(d => d?.expiration_status === 'upcoming')?.length || 0,
        valid: data?.filter(d => d?.expiration_status === 'valid')?.length || 0,
        byType: {
          workPermit: data?.filter(d => d?.document_type === 'work_permit')?.length || 0,
          visa: data?.filter(d => d?.document_type === 'visa')?.length || 0,
          driverLicense: data?.filter(d => d?.document_type === 'driver_license')?.length || 0,
          passport: data?.filter(d => d?.document_type === 'passport')?.length || 0
        }
      };

      return stats;
    } catch (error) {
      console.error('Error getting expiration statistics:', error);
      throw error;
    }
  },

  async getExpirationsByDepartment() {
    try {
      const { data, error } = await supabase
        ?.from('employee_documents')
        ?.select(`
          expiration_status,
          document_type,
          employees (
            department
          )
        `)
        ?.not('expiry_date', 'is', null)
        ?.in('document_type', ['work_permit', 'visa', 'driver_license', 'passport']);

      if (error) throw error;

      // Group by department
      const departmentStats = {};
      data?.forEach(doc => {
        const dept = doc?.employees?.department || 'Unassigned';
        if (!departmentStats?.[dept]) {
          departmentStats[dept] = {
            department: dept,
            total: 0,
            expired: 0,
            critical: 0,
            warning: 0,
            upcoming: 0,
            valid: 0
          };
        }
        departmentStats[dept].total++;
        departmentStats[dept][doc?.expiration_status]++;
      });

      return Object.values(departmentStats);
    } catch (error) {
      console.error('Error getting expirations by department:', error);
      throw error;
    }
  },

  // ============================================================================
  // MANUAL REMINDER TRIGGER
  // ============================================================================

  async triggerExpirationCheck() {
    try {
      const { error } = await supabase?.rpc('check_expiring_documents');

      if (error) throw error;

      return { success: true, message: 'Expiration check completed successfully' };
    } catch (error) {
      console.error('Error triggering expiration check:', error);
      throw error;
    }
  },

  // ============================================================================
  // DOCUMENT UPDATE
  // ============================================================================

  async updateDocumentExpiry(documentId, expiryDate) {
    try {
      const { data, error } = await supabase
        ?.from('employee_documents')
        ?.update({
          expiry_date: expiryDate,
          renewal_reminder_sent: false,
          renewal_reminder_sent_at: null
        })
        ?.eq('id', documentId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        expiryDate: data?.expiry_date,
        daysUntilExpiry: data?.days_until_expiry,
        expirationStatus: data?.expiration_status
      };
    } catch (error) {
      console.error('Error updating document expiry:', error);
      throw error;
    }
  },

  // ============================================================================
  // REAL-TIME EMAIL NOTIFICATION SERVICE
  // ============================================================================

  async sendDocumentExpirationEmail(documentId) {
    try {
      // Fetch document details with employee information
      const { data: document, error: fetchError } = await supabase
        ?.from('employee_documents')
        ?.select(`
          *,
          employees (
            employee_number,
            first_name,
            last_name,
            email,
            department
          )
        `)
        ?.eq('id', documentId)
        ?.single();

      if (fetchError) throw fetchError;
      if (!document) throw new Error('Document not found');

      const employee = document?.employees;
      if (!employee?.email) {
        throw new Error('Employee email not found');
      }

      // Call Edge Function to send email
      const { data, error } = await supabase?.functions?.invoke(
        'send-document-expiration-alert',
        {
          body: {
            documentId: document?.id,
            employeeName: `${employee?.first_name} ${employee?.last_name}`,
            employeeEmail: employee?.email,
            documentType: document?.document_type,
            documentName: document?.document_name,
            expiryDate: document?.expiry_date,
            daysUntilExpiry: document?.days_until_expiry,
            department: employee?.department
          }
        }
      );

      if (error) throw error;

      return {
        success: true,
        message: 'Email notification sent successfully',
        data
      };
    } catch (error) {
      console.error('Error sending document expiration email:', error);
      throw error;
    }
  },

  async processEmailNotificationQueue() {
    try {
      // Fetch pending email notifications from queue
      const { data: notifications, error: fetchError } = await supabase
        ?.from('notification_queue')
        ?.select('*')
        ?.eq('notification_status', 'pending')
        ?.eq('notification_type', 'compliance_alert')
        ?.filter('metadata->>send_email', 'eq', 'true')
        ?.filter('metadata->>email_type', 'eq', 'document_expiration_alert')
        ?.lte('scheduled_for', new Date()?.toISOString())
        ?.order('priority', { ascending: false })
        ?.order('created_at', { ascending: true })
        ?.limit(10);

      if (fetchError) throw fetchError;
      if (!notifications || notifications?.length === 0) {
        return { success: true, processed: 0 };
      }

      const results = [];

      // Process each notification
      for (const notification of notifications) {
        try {
          const metadata = notification?.metadata || {};
          
          // Call Edge Function to send email
          const { data, error } = await supabase?.functions?.invoke(
            'send-document-expiration-alert',
            {
              body: {
                documentId: metadata?.document_id,
                employeeName: metadata?.employee_name,
                employeeEmail: metadata?.employee_email,
                documentType: metadata?.document_type,
                documentName: metadata?.document_name,
                expiryDate: metadata?.expiry_date,
                daysUntilExpiry: metadata?.days_until_expiry,
                department: metadata?.department
              }
            }
          );

          if (error) throw error;

          // Update notification status
          await supabase
            ?.from('notification_queue')
            ?.update({
              notification_status: 'delivered',
              sent_at: new Date()?.toISOString(),
              delivered_at: new Date()?.toISOString(),
              updated_at: new Date()?.toISOString()
            })
            ?.eq('id', notification?.id);

          results?.push({
            notificationId: notification?.id,
            success: true,
            emailId: data?.emailId
          });
        } catch (error) {
          console.error(`Error processing notification ${notification?.id}:`, error);
          
          // Update notification with error
          await supabase
            ?.from('notification_queue')
            ?.update({
              notification_status: 'failed',
              error_message: error?.message,
              retry_count: (notification?.retry_count || 0) + 1,
              updated_at: new Date()?.toISOString()
            })
            ?.eq('id', notification?.id);

          results?.push({
            notificationId: notification?.id,
            success: false,
            error: error?.message
          });
        }
      }

      return {
        success: true,
        processed: notifications?.length,
        results
      };
    } catch (error) {
      console.error('Error processing email notification queue:', error);
      throw error;
    }
  },

  // ============================================================================
  // REAL-TIME SUBSCRIPTIONS
  // ============================================================================

  subscribeToDocumentExpirationChanges(callback) {
    try {
      const subscription = supabase
        ?.channel('document-expiration-changes')
        ?.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'employee_documents',
            filter: 'document_type=in.(work_permit,visa,driver_license,passport)'
          },
          async (payload) => {
            console.log('Document expiration change detected:', payload);
            
            // Check if document is in warning/critical/expired status
            const document = payload?.new;
            if (document && ['critical', 'warning', 'expired']?.includes(document?.expiration_status)) {
              // Trigger email notification
              try {
                await documentExpirationService?.sendDocumentExpirationEmail(document?.id);
              } catch (error) {
                console.error('Error sending real-time email notification:', error);
              }
            }
            
            // Call the callback with the payload
            if (callback) {
              callback(payload);
            }
          }
        )
        ?.subscribe();

      return subscription;
    } catch (error) {
      console.error('Error subscribing to document expiration changes:', error);
      throw error;
    }
  },

  subscribeToNotificationQueue(callback) {
    try {
      const subscription = supabase
        ?.channel('notification-queue-changes')
        ?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notification_queue',
            filter: 'notification_type=eq.compliance_alert'
          },
          async (payload) => {
            console.log('New notification queued:', payload);
            
            const notification = payload?.new;
            const metadata = notification?.metadata || {};
            
            // Auto-process document expiration email notifications
            if (metadata?.send_email && metadata?.email_type === 'document_expiration_alert') {
              try {
                await documentExpirationService?.processEmailNotificationQueue();
              } catch (error) {
                console.error('Error auto-processing notification queue:', error);
              }
            }
            
            // Call the callback with the payload
            if (callback) {
              callback(payload);
            }
          }
        )
        ?.subscribe();

      return subscription;
    } catch (error) {
      console.error('Error subscribing to notification queue:', error);
      throw error;
    }
  },

  unsubscribe(subscription) {
    try {
      if (subscription) {
        supabase?.removeChannel(subscription);
      }
    } catch (error) {
      console.error('Error unsubscribing:', error);
    }
  }
};

export default documentExpirationService;